import { useCoreAuth } from '@carvana/core-auth';
import { datadogRum } from '@datadog/browser-rum';
import Cookies from 'js-cookie';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import Loading from '|/components/base/loading';
import { Token, default as getAccessToken } from '|/helpers/access-token';
import googleTagManager from '|/helpers/analytics/google-tag-manager';
import useAuthBoot from '|/hooks/use-auth-boot';

interface LoginState {
  isUserLoggedIn: boolean;
  isImpersonating: boolean;
  sub: string;
  user_id: string;
  email?: string;
  name: string;
}

const controlLoginState = (accessToken: Token | null): LoginState => {
  const user = accessToken?.user ?? ({} as Token['user']);
  return {
    isUserLoggedIn: user.name !== undefined,
    isImpersonating: user.is_impersonating === 'True',
    sub: user.sub,
    user_id: user.user_id,
    email: user.email,
    name: user.name
  };
};

// Only mounted for treatment — keeps control-arm sessions from ever calling useCoreAuth.
const CoreAuthLoginState: React.FC<{ onChange: (state: LoginState) => void }> = ({ onChange }) => {
  const { getUserState } = useCoreAuth();

  useEffect(() => {
    const userState = getUserState();
    onChange({
      isUserLoggedIn: userState.isLoggedIn,
      isImpersonating: userState.impersonation.is_impersonating,
      sub: userState.user.sub,
      user_id: userState.user.user_id,
      email: userState.user.email,
      name: userState.user.name
    });
  }, [getUserState, onChange]);

  return null;
};

const defaultContext = {
  accessToken: null as null | ReturnType<typeof getAccessToken>,
  isImpersonating: false
};

const UserInitializationContext = createContext(defaultContext);

const UserInitializationProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { isUserInitialized, accessToken, isInTreatment, isExperimentSettled } = useAuthBoot();
  const [treatmentLoginState, setTreatmentLoginState] = useState<LoginState | null>(null);

  // isInTreatment alone can't distinguish "still loading" from "confirmed control".
  const isControlArm = isExperimentSettled && !isInTreatment;
  const isTreatmentLoginStateReady =
    isExperimentSettled && isInTreatment && treatmentLoginState !== null;
  const isLoginStateSettled = isControlArm || isTreatmentLoginStateReady;

  // Falls back to the local token's read until core-auth's arrives, so consumers never see a false negative.
  const loginState = useMemo(
    () => (isTreatmentLoginStateReady ? treatmentLoginState : controlLoginState(accessToken)),
    [accessToken, isTreatmentLoginStateReady, treatmentLoginState]
  );
  const { isImpersonating } = loginState;

  useEffect(() => {
    if (accessToken === null || !isLoginStateSettled) return;

    if (loginState.isUserLoggedIn)
      googleTagManager.trackUser('User Logged In', {
        userId: loginState.sub
      });
    datadogRum.setUser({
      id: loginState.user_id,
      browserCookieId: Cookies.get('BrowserCookieId')
    });
  }, [accessToken, isLoginStateSettled, loginState]);

  if (!isUserInitialized) return <Loading />;

  return (
    <UserInitializationContext.Provider value={{ accessToken, isImpersonating }}>
      {isInTreatment && <CoreAuthLoginState onChange={setTreatmentLoginState} />}
      {children}
    </UserInitializationContext.Provider>
  );
};

export default UserInitializationContext;
export { UserInitializationProvider };
